(() => {
  // The asset can appear more than once when sections are added in the editor.
  if (window.charbonMenuLoaded) return;
  window.charbonMenuLoaded = true;

  const instances = new WeakMap();

  function init(section) {
    const panels = [...section.querySelectorAll('[data-charbon-menu-panel]')];
    const tablist = section.querySelector('[role="tablist"]');
    if (!panels.length || !tablist) return;
    const previous = instances.get(section);
    if (
      previous?.tablist === tablist &&
      previous.panels.length === panels.length &&
      previous.panels.every((panel, index) => panel === panels[index]) &&
      previous.tabs.every((tab) => tab.parentElement === tablist)
    ) return;

    // A section refresh can keep the outer element while replacing its children.
    previous?.controller.abort();
    const controller = new AbortController();
    const { signal } = controller;
    tablist.replaceChildren();

    function activate(index, focus = false) {
      panels.forEach((panel, i) => {
        const selected = i === index;
        panel.hidden = !selected;
        tabs[i].classList.toggle('is-active', selected);
        tabs[i].setAttribute('aria-selected', String(selected));
        tabs[i].tabIndex = selected ? 0 : -1;
      });
      if (focus) tabs[index].focus();
    }

    const tabs = panels.map((panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'charbon-menu__tab';
      tab.id = `${panel.id}-tab`;
      tab.textContent = panel.dataset.menuTitle;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % panels.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + panels.length) % panels.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = panels.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        activate(next, true);
      });
      tablist.append(tab);
      return tab;
    });

    instances.set(section, { panels, tabs, tablist, controller });
    section.dataset.menuInitialized = 'true';
    tablist.hidden = false;
    activate(0);
    section.addEventListener('charbon:menu:hash', () => {
      const hash = window.location.hash.slice(1);
      const byName = hash.startsWith('menu-tab=');
      let anchor;
      try {
        anchor = decodeURIComponent(byName ? hash.slice('menu-tab='.length).replace(/\+/g, ' ') : hash);
      } catch { return; }
      if (!anchor) return;
      const normalize = (value) => value.normalize('NFC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('nl');
      const index = panels.findIndex((panel) => byName
        ? normalize(panel.dataset.menuTitle) === normalize(anchor)
        : panel.dataset.menuAnchor === anchor || panel.id === anchor
      );
      // A renamed or removed destination still brings visitors to the menu.
      if (index < 0 && !byName) return;
      const activeIndex = index < 0 ? 0 : index;
      activate(activeIndex);
      const scrollToTab = () => {
        if (!section.isConnected || panels[activeIndex].hidden) return;
        requestAnimationFrame(() => tablist.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        }));
      };
      scrollToTab();
      // Images above the menu can still shift its position during navigation.
      if (document.readyState !== 'complete') {
        window.addEventListener('load', scrollToTab, { once: true, signal });
      }
    }, { signal });
    section.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest('[data-charbon-menu-panel]');
      const index = panels.indexOf(panel);
      if (index >= 0) activate(index);
    }, { signal });
    section.dispatchEvent(new Event('charbon:menu:hash'));
  }

  function initAll() {
    document.querySelectorAll('.charbon-menu').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
  // Local hot reload and dynamically inserted sections need not emit editor events.
  const observer = new MutationObserver((records) => {
    const menuChanged = records.some((record) =>
      [...record.addedNodes].some((node) => node instanceof Element && (
        node.matches('.charbon-menu') ||
        node.closest('.charbon-menu') ||
        node.querySelector('.charbon-menu')
      ))
    );
    if (menuChanged) initAll();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('hashchange', () => {
    document.querySelectorAll('.charbon-menu').forEach((section) => {
      section.dispatchEvent(new Event('charbon:menu:hash'));
    });
  });
})();
